/**
 * @file Detail-view dirty state and local draft preservation helpers.
 */

import { state } from '../../app/state/state.js';
import {
    getRegisteredControlValues,
    updateControlValue,
} from '../ui/controls/controlCore.js';

const DETAIL_LIVE_SAVE_FIELDS = new Set([
    'assigned_users',
    'equipment_status',
    'priority',
    'project_manager',
    'provider',
    'status',
    'types',
]);
const DETAIL_DRAFT_EXCLUDED_FIELDS = new Set(['types_validator']);

let detailDirtyItemId = null;
const detailDirtyFields = new Set();
const detailFormBaseline = new Map();

/**
 * Keeps the dirty-field tracker scoped to the currently selected detail item.
 */
export function syncDetailDirtyScope() {
    if (detailDirtyItemId === state.selectedItemId) {return;}
    detailDirtyItemId = state.selectedItemId || null;
    detailDirtyFields.clear();
    detailFormBaseline.clear();
}

/**
 * Clears tracked dirty fields and baseline state after save or discard.
 */
export function clearDetailDirtyFields() {
    detailDirtyItemId = null;
    detailDirtyFields.clear();
    detailFormBaseline.clear();
}

/**
 * Tracks one or more manually saved fields as dirty.
 * @param {string|string[]} fieldNames Field name or names.
 */
export function markDetailDirtyFields(fieldNames) {
    syncDetailDirtyScope();
    const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    names
        .filter(name => typeof name === 'string' && name.trim())
        .forEach(name => detailDirtyFields.add(name));
}

function isDraftableDetailField(element) {
    if (!element?.name || element.type === 'file') {return false;}
    if (DETAIL_DRAFT_EXCLUDED_FIELDS.has(element.name)) {return false;}
    return !DETAIL_LIVE_SAVE_FIELDS.has(element.name);
}

function getDetailFieldDraftEntry(element) {
    if (element.type === 'checkbox' || element.type === 'radio') {
        return {
            checked: element.checked,
            name: element.name,
            type: element.type,
            value: element.value,
        };
    }

    if (element.tagName === 'SELECT' && element.multiple) {
        return {
            name: element.name,
            selectedValues: Array.from(element.selectedOptions).map(option => option.value),
            type: 'select-multiple',
        };
    }

    return {
        name: element.name,
        type: element.type || element.tagName.toLowerCase(),
        value: element.value,
    };
}

function getRegisteredControlDraftEntries(formElement) {
    const extraction = getRegisteredControlValues(formElement);
    return Object.entries(extraction.values)
        .filter(([name]) => detailDirtyFields.has(name))
        .map(([name, value]) => ({
            name,
            type: 'registered-control',
            value,
        }));
}

function getDetailFieldNames(formElement) {
    return [...new Set(Array.from(formElement?.elements || [])
        .filter(isDraftableDetailField)
        .map(element => element.name))];
}

function getDetailFieldSnapshot(formElement, fieldName) {
    return Array.from(formElement?.elements || [])
        .filter(isDraftableDetailField)
        .filter(element => element.name === fieldName)
        .map(getDetailFieldDraftEntry);
}

/**
 * Records the current detail form state as the non-dirty baseline.
 * @param {HTMLFormElement|null} formElement Current detail form.
 */
export function recordDetailFormBaseline(formElement) {
    syncDetailDirtyScope();
    detailFormBaseline.clear();
    getDetailFieldNames(formElement).forEach(fieldName => {
        detailFormBaseline.set(
            fieldName,
            JSON.stringify(getDetailFieldSnapshot(formElement, fieldName)),
        );
    });
}

/**
 * Records one detail form field as clean without clearing other dirty fields.
 * @param {HTMLFormElement|null} formElement Current detail form.
 * @param {string} fieldName Field name to refresh in the baseline.
 */
export function recordDetailFieldBaseline(formElement, fieldName) {
    if (!formElement || !fieldName) {return;}
    syncDetailDirtyScope();
    detailFormBaseline.set(
        fieldName,
        JSON.stringify(getDetailFieldSnapshot(formElement, fieldName)),
    );
}

function getBaselineChangedDetailFields(formElement) {
    return getDetailFieldNames(formElement)
        .filter(fieldName => detailFormBaseline.has(fieldName))
        .filter(fieldName => detailFormBaseline.get(fieldName) !==
            JSON.stringify(getDetailFieldSnapshot(formElement, fieldName)));
}

function syncNativeDetailDirtyFields(formElement) {
    syncDetailDirtyScope();
    getDetailFieldNames(formElement)
        .filter(fieldName => detailFormBaseline.has(fieldName))
        .forEach(fieldName => {
            const hasChanged = detailFormBaseline.get(fieldName) !==
                JSON.stringify(getDetailFieldSnapshot(formElement, fieldName));
            if (hasChanged) {
                detailDirtyFields.add(fieldName);
            } else {
                detailDirtyFields.delete(fieldName);
            }
        });
}

/**
 * Returns whether a native-backed detail field has unsaved changes.
 * @param {HTMLFormElement|null} formElement Current detail form.
 * @param {string} fieldName Field name to inspect.
 * @returns {boolean} Whether the field is dirty.
 */
export function isDetailFieldDirty(formElement, fieldName) {
    if (!formElement || !fieldName) {return false;}
    syncNativeDetailDirtyFields(formElement);
    return detailDirtyFields.has(fieldName);
}

/**
 * Clears one field from the dirty tracker without affecting other fields.
 * @param {string} fieldName Field name to clear.
 */
export function clearDetailDirtyField(fieldName) {
    if (!fieldName) {return;}
    syncDetailDirtyScope();
    detailDirtyFields.delete(fieldName);
}

/**
 * Returns whether any detail fields remain dirty.
 * @param {HTMLFormElement|null} formElement Current detail form.
 * @returns {boolean} Whether any tracked fields are dirty.
 */
export function hasDetailDirtyFields(formElement) {
    if (!formElement) {return detailDirtyFields.size > 0;}
    syncNativeDetailDirtyFields(formElement);
    return detailDirtyFields.size > 0;
}

/**
 * Captures transient detail UI state before a listener-driven re-render.
 * @param {HTMLElement|null} detailsViewContainer Detail view container.
 * @returns {{recurrenceEditorOpen: boolean, recurrenceShellHidden: boolean}}
 */
export function captureDetailUiDraft(detailsViewContainer) {
    const recurrenceShell = detailsViewContainer?.querySelector(
        '#task-detail-recurrence-shell',
    );
    const recurrenceEditor = detailsViewContainer?.querySelector(
        '#task-detail-recurrence-editor',
    );

    return {
        recurrenceEditorOpen: Boolean(
            recurrenceEditor && !recurrenceEditor.classList.contains('hidden'),
        ),
        recurrenceShellHidden: Boolean(
            recurrenceShell?.classList.contains('hidden'),
        ),
    };
}

/**
 * Captures dirty manual-save form values before a detail re-render.
 * @param {HTMLFormElement|null} formElement Current detail form.
 * @returns {Array<object>|null}
 */
export function captureDetailFormDraft(formElement) {
    if (!formElement) {return null;}
    syncDetailDirtyScope();
    markDetailDirtyFields(getBaselineChangedDetailFields(formElement));
    if (detailDirtyFields.size === 0) {return null;}

    const nativeEntries = Array.from(formElement.elements)
        .filter(isDraftableDetailField)
        .filter(element => detailDirtyFields.has(element.name))
        .map(getDetailFieldDraftEntry);
    const registeredEntries = getRegisteredControlDraftEntries(formElement);

    return [
        ...nativeEntries,
        ...registeredEntries,
    ];
}

/**
 * Restores dirty manual-save form values after a detail re-render.
 * @param {HTMLFormElement|null} formElement Current detail form.
 * @param {Array<object>|null} draft Captured draft entries.
 */
export function restoreDetailFormDraft(formElement, draft) {
    if (!formElement || !Array.isArray(draft)) {return;}

    const registeredEntries = [];

    draft.forEach(entry => {
        if (entry.type === 'registered-control') {
            registeredEntries.push(entry);
            return;
        }

        const elements = Array.from(formElement.elements)
            .filter(element => element.name === entry.name);

        elements.forEach(element => {
            if (entry.type === 'checkbox' || entry.type === 'radio') {
                if (elements.length > 1 && element.value !== entry.value) {return;}
                element.checked = entry.checked;
                return;
            }

            if (entry.type === 'select-multiple') {
                Array.from(element.options || []).forEach(option => {
                    option.selected = entry.selectedValues.includes(option.value);
                });
                return;
            }

            element.value = entry.value;
        });
    });

    if (registeredEntries.length === 0) {return;}

    const extraction = getRegisteredControlValues(formElement);
    registeredEntries.forEach(entry => {
        const controlId = extraction.sources[entry.name];
        if (controlId) {
            updateControlValue(controlId, entry.value, { silent: true });
        }
    });
}

/**
 * Enables detail manual-save buttons and marks the form dirty.
 * @param {HTMLElement} detailsViewContainer Detail view container.
 * @param {string|string[]} fieldNames Field name or names.
 */
export function enableDetailSaveButtons(detailsViewContainer, fieldNames = []) {
    markDetailDirtyFields(fieldNames);
    state.hasUnsavedChanges = true;
    detailsViewContainer.querySelectorAll('.details-save-btn').forEach(btn => {
        btn.classList.remove(
            'bg-gray-200',
            'hover:bg-gray-300',
            'text-gray-500',
            'text-gray-800',
        );
        btn.classList.add(
            'bg-[var(--dfc-brand-primary)]',
            'hover:bg-[var(--dfc-brand-primary-hover)]',
            'text-gray-900',
        );
        btn.disabled = false;
        btn.classList.add('shadow-md');
    });
}
